import {
    type KeyboardEvent,
    type MouseEvent,
    type PointerEvent,
} from 'react';

import { format } from 'date-fns';

import CancelIcon from '@crm/shared/assets/icons/cancel-01.svg?react';
import FilterIcon from '@crm/shared/assets/icons/filter.svg?react';
import { tw } from '@crm/shared/lib';
import { DEFAULT_END_MONTH, DEFAULT_START_MONTH } from '@crm/shared/ui/DatePicker/model/consts';

import { useFilterDatePicker } from './model/useFilterDatePicker';
import { Button } from '../Button';
import { Calendar } from '../Calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../Popover';
import { SvgIcon } from '../SvgIcon';

import type { DateRange } from 'react-day-picker';

interface FilterDatePickerProps {
    labelText: string;
    value?: DateRange;
    onApply: (date?: DateRange) => void;
}

export const FilterDatePicker = ({ labelText, value, onApply }: FilterDatePickerProps) => {
    const {
        applyDateRangeAndClose,
        clearDateRange,
        draftDateRange,
        handleOpenChange,
        isOpen,
        selectDateRange,
    } = useFilterDatePicker({ value, onApply });
    const dateBadgeContainer = tw('flex items-center gap-1.5 rounded-sm bg-zinc-100 px-1 py-0.5');
    const dateBadgeStyles = tw('leading-none text-smtr-main-black-100');

    const handleCalendarKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key !== 'Enter') {
            return;
        }

        event.preventDefault();
        applyDateRangeAndClose();
    };

    const onClearDatePointerDown = (event: PointerEvent<HTMLSpanElement>) => {
        event.stopPropagation();
    };

    const onClearDate = (event: MouseEvent<HTMLSpanElement>) => {
        event.stopPropagation();
        clearDateRange();
    };

    const dateBadgeLabel = value?.from
        ? [format(value.from, 'dd.MM.yyyy'), value.to ? format(value.to, 'dd.MM.yyyy') : null]
              .filter(Boolean)
              .join(' - ')
        : null;

    return (
        <Popover open={isOpen} onOpenChange={handleOpenChange}>
            <PopoverTrigger
                render={
                    <Button
                        variant="outline"
                        className="flex items-center justify-start gap-3.5 px-3"
                    >
                        <div className="flex items-center gap-2">
                            <SvgIcon icon={FilterIcon} />
                            <span className="text-smtr-main-black-100">{labelText}</span>
                        </div>
                        {dateBadgeLabel ? (
                            <div className={dateBadgeContainer}>
                                <span className={dateBadgeStyles}>{dateBadgeLabel}</span>
                                <span onPointerDown={onClearDatePointerDown} onClick={onClearDate}>
                                    <SvgIcon icon={CancelIcon} size="16" />
                                </span>
                            </div>
                        ) : null}
                    </Button>
                }
            />
            <PopoverContent className="w-auto p-0" align="start" onKeyDown={handleCalendarKeyDown}>
                <Calendar
                    mode="range"
                    resetOnSelect
                    defaultMonth={draftDateRange?.from}
                    selected={draftDateRange}
                    onSelect={selectDateRange}
                    numberOfMonths={2}
                    captionLayout="dropdown"
                    startMonth={DEFAULT_START_MONTH}
                    endMonth={DEFAULT_END_MONTH}
                />
            </PopoverContent>
        </Popover>
    );
};
